import { useAppStore } from '@/stores/appStore';
import type { ScrapeJob } from '@/types';

type Runner = (update: (progress: number, message: string) => void) => Promise<void>;

type QueueItem = {
  id: string;
  run: Runner;
};

const MAX_CONCURRENT = 3;
let running = 0;
const queue: QueueItem[] = [];

function nowIso() {
  return new Date().toISOString();
}

function makeId() {
  return `${Date.now()}-${Math.random().toString(16).slice(2, 8)}`;
}

function updateJob(id: string, updates: Partial<ScrapeJob>) {
  useAppStore.getState().updateScrapeJob(id, updates);
}

async function runNext() {
  if (running >= MAX_CONCURRENT) return;
  const next = queue.shift();
  if (!next) return;
  running += 1;

  updateJob(next.id, {
    status: 'running',
    startedAt: nowIso(),
    progress: 5,
    message: 'Starting scrape…',
  });

  try {
    await next.run((progress, message) => {
      updateJob(next.id, { progress, message });
    });
    updateJob(next.id, {
      status: 'succeeded',
      progress: 100,
      message: 'Complete',
      finishedAt: nowIso(),
    });
  } catch (err) {
    updateJob(next.id, {
      status: 'failed',
      progress: 100,
      message: 'Failed',
      error: err instanceof Error ? err.message : 'Unknown error',
      finishedAt: nowIso(),
    });
  } finally {
    running -= 1;
    runNext();
  }
}

export function enqueueScrapeJob(meta: Omit<ScrapeJob, 'id' | 'status' | 'progress' | 'message' | 'queuedAt'>, run: Runner) {
  const id = makeId();
  const job: ScrapeJob = {
    id,
    status: 'queued',
    progress: 0,
    message: 'Queued',
    queuedAt: nowIso(),
    ...meta,
  };
  useAppStore.getState().addScrapeJob(job);
  queue.push({ id, run });
  runNext();
  return id;
}

export function getScrapeConcurrency() {
  return { running, queued: queue.length, max: MAX_CONCURRENT };
}
